import React, { useEffect, useState } from "react";
import useContract from "../hooks/useContract";
import { useAppKitAccount } from "@reown/appkit/react";
import { toast } from "react-hot-toast";

const Admin = () => {
  const { address } = useAppKitAccount();

  const {
    isAdmin,
    createPackage,
    setPackageStatus,
    addAirdrop,
    setAllowedAirdropToken,
    setWallets,
    setReferralStatus,
    setTaxConfig, // ✅ ADDED
    pause,
    unpause,
  } = useContract();

  const [admin, setAdmin] = useState(false);
  const [loading, setLoading] = useState(false);

  const [pkgName, setPkgName] = useState("");
  const [pkgPrice, setPkgPrice] = useState("");

  const [pid, setPid] = useState("");
  const [status, setStatus] = useState(true);

  const [token, setToken] = useState("");
  const [airPid, setAirPid] = useState("");
  const [amount, setAmount] = useState("");

  const [allowToken, setAllowToken] = useState("");
  const [allowStatus, setAllowStatus] = useState(true);

  const [treasury, setTreasury] = useState("");
  const [marketing, setMarketing] = useState("");

  // ✅ TAX STATES
  const [tax, setTax] = useState("");
  const [burn, setBurn] = useState("");
  const [marketingTax, setMarketingTax] = useState("");
  const [pool, setPool] = useState("");

  // 🔥 CHECK ADMIN
  useEffect(() => {
    if (!address) return;

    const check = async () => {
      const res = await isAdmin();
      setAdmin(res);
    };

    check();
  }, [address, isAdmin]);

  // ✅ FIXED TOAST WRAPPER
  const handle = async (fn, successMsg = "Success ✅") => {
    const t = toast.loading("Processing transaction...");

    try {
      setLoading(true);

      await fn();

      toast.dismiss(t); // ✅ FIX
      toast.success(successMsg);

    } catch (err) {
      console.log(err);

      toast.dismiss(t); // ✅ FIX
      toast.error("Transaction failed ❌");
    }

    setLoading(false);
  };

  if (!address) {
    return <div className="text-center mt-20 text-gray-400">Connect wallet</div>;
  }

  if (!admin) {
    return <div className="text-center mt-20 text-red-400">❌ You are not admin</div>;
  }

  return (
    <div className="text-white p-4 md:p-6">

      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-yellow-400">
        Admin Control Panel
      </h1>

      {/* CREATE PACKAGE */}
      <Section title="📦 Create Package">
        <Input value={pkgName} set={setPkgName} placeholder="Package Name" />
        <Input value={pkgPrice} set={setPkgPrice} placeholder="Price (KIND)" />
        <Button onClick={() => handle(() => createPackage(pkgName, pkgPrice), "Package created 🎉")}>
          Create Package
        </Button>
      </Section>

      {/* PACKAGE STATUS */}
      <Section title="⚙️ Package Status">
        <Input value={pid} set={setPid} placeholder="Package ID" />

        <select
          onChange={(e) => setStatus(e.target.value === "true")}
          className="w-full px-4 py-3 rounded-xl bg-[#0f172a] border border-gray-700 text-white"
        >
          <option value="true">✅ Activate</option>
          <option value="false">❌ Deactivate</option>
        </select>

        <Button onClick={() => handle(() => setPackageStatus(pid, status), "Status updated ⚙️")}>
          Update Status
        </Button>
      </Section>

      {/* TAX CONFIG 🔥 */}
      <Section title="🔥 Tax Configuration">
        <Input value={tax} set={setTax} placeholder="Tax (e.g 450 = 45%)" />
        <Input value={burn} set={setBurn} placeholder="Burn %" />
        <Input value={marketingTax} set={setMarketingTax} placeholder="Marketing %" />
        <Input value={pool} set={setPool} placeholder="Pool %" />

        <Button
          onClick={() =>
            handle(
              () => setTaxConfig(tax, burn, marketingTax, pool),
              "Tax updated 🔥"
            )
          }
        >
          Update Tax
        </Button>
      </Section>

      {/* AIRDROP TOKEN WHITELIST */}
      <Section title="🛡️ Allow Airdrop Token">
        <Input value={allowToken} set={setAllowToken} placeholder="Token Address" />

        <select
          onChange={(e) => setAllowStatus(e.target.value === "true")}
          className="w-full px-4 py-3 rounded-xl bg-[#0f172a] border border-gray-700 text-white"
        >
          <option value="true">✅ Allow</option>
          <option value="false">❌ Remove</option>
        </select>

        <Button
          onClick={() =>
            handle(
              () => setAllowedAirdropToken(allowToken, allowStatus),
              "Token permission updated 🛡️"
            )
          }
        >
          Update Token Permission
        </Button>
      </Section>

      {/* ADD AIRDROP */}
      <Section title="🎁 Add Airdrop">
        <Input value={token} set={setToken} placeholder="Token Address" />
        <Input value={airPid} set={setAirPid} placeholder="Package ID" />
        <Input value={amount} set={setAmount} placeholder="Amount" />

        <Button
          onClick={() =>
            handle(
              () => addAirdrop(token, airPid, amount),
              "Airdrop added 🎁"
            )
          }
        >
          Add Airdrop
        </Button>

        <p className="text-xs text-gray-400">
          ⚠️ Token must be allowed first
        </p>
      </Section>

      {/* SYSTEM */}
      <Section title="🧠 System Controls">
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => handle(pause, "System paused 🛑")}>
            Pause
          </Button>

          <Button onClick={() => handle(unpause, "System resumed ▶️")}>
            Unpause
          </Button>

          <Button onClick={() => handle(() => setReferralStatus(true), "Referral enabled 👥")}>
            Enable Referral
          </Button>

          <Button onClick={() => handle(() => setReferralStatus(false), "Referral disabled 🚫")}>
            Disable Referral
          </Button>
        </div>
      </Section>

      {/* WALLETS */}
      <Section title="💰 Set Wallets">
        <Input value={treasury} set={setTreasury} placeholder="Treasury Address" />
        <Input value={marketing} set={setMarketing} placeholder="Marketing Address" />

        <Button
          onClick={() =>
            handle(
              () => setWallets(treasury, marketing),
              "Wallets updated 💰"
            )
          }
        >
          Update Wallets
        </Button>
      </Section>

      {loading && (
        <div className="text-center mt-6 text-blue-400">
          Processing transaction...
        </div>
      )}
    </div>
  );
};

// COMPONENTS (UNCHANGED)

const Section = ({ title, children }) => (
  <div className="mb-8 bg-gradient-to-br from-[#0f1b2d] to-[#1a0f1f] border border-gray-700 p-5 rounded-2xl">
    <h2 className="mb-4 text-lg text-blue-400">{title}</h2>
    <div className="flex flex-col gap-3">{children}</div>
  </div>
);

const Input = ({ value, set, placeholder }) => (
  <input
    value={value}
    onChange={(e) => set(e.target.value)}
    placeholder={placeholder}
    className="w-full p-3 bg-black border border-gray-700 rounded-lg text-sm"
  />
);

const Button = ({ children, onClick }) => (
  <button
    onClick={onClick}
    className="bg-gradient-to-r from-blue-500 to-cyan-500 px-4 py-2 rounded-lg font-semibold hover:scale-105 transition"
  >
    {children}
  </button>
);

export default Admin;